import { LikeResponse, LikeRequest } from '@ai-platform/ai-utils';
import { api, ApiTags } from '../../api';
import { PromptCard, PromptDto } from '../../../components/types';

import { PromptRequest, PromptResponse, MyPromptsResponse } from './types';
import { transformPromptDtoToModel, transformPromptsDtoToCards } from './mappers';

/**
 * Обновляет лайки и статус лайка для промпта
 * @param prompt - промпт для обновления
 * @param likeDelta - изменение количества лайков (+1 или -1)
 * @param isLiked - новое состояние лайка
 */
//eslint-disable-next-line @typescript-eslint/no-explicit-any
const updatePromptLikes = (prompt: any, likeDelta: number, isLiked: boolean) => {
    if (prompt) {
        prompt.likes = (prompt.likes || 0) + likeDelta;
        prompt.isLiked = isLiked;
    }
};

const promptApi = api.injectEndpoints({
    endpoints: build => ({
        // Получить промпт по id
        getPrompt: build.query<PromptResponse, PromptRequest>({
            query: ({ promptId }) => `/prompts/${promptId}`,
            transformResponse: transformPromptDtoToModel,
            providesTags: (result, _, request) => (result ? [{ type: ApiTags.Prompts, id: request.promptId }] : []),
        }),

        // Получить похожие промпты по id промпта
        getSimilarPrompts: build.query<PromptCard[], PromptRequest>({
            query: ({ promptId }) => ({ url: '/prompts', params: { hash: promptId } }),
            transformResponse: transformPromptsDtoToCards,
            providesTags: result => {
                if (!result) {
                    return [{ type: ApiTags.Prompts, id: 'LIST' }];
                }

                return [
                    ...result.map(({ id }) => ({ type: ApiTags.Prompts, id })),
                    { type: ApiTags.Prompts, id: 'LIST' },
                ];
            },
        }),

        // Получить 3 популярных промпта
        getPopularPrompts: build.query<PromptCard[], void>({
            query: () => '/prompts/popular',
            transformResponse: (response: PromptDto[]) =>
                transformPromptsDtoToCards({ prompts: response, next_cursor: '' }),
            providesTags: [{ type: ApiTags.Prompts, id: 'POPULAR' }],
        }),

        // Получить все промпты текущего пользователя
        getMyPrompts: build.query<PromptCard[], void>({
            query: () => '/prompts/user/my',
            transformResponse: (response: MyPromptsResponse) =>
                transformPromptsDtoToCards({ prompts: response, next_cursor: '' }),
            providesTags: [{ type: ApiTags.Prompts, id: 'MY' }],
            keepUnusedDataFor: 0,
        }),

        // Поставить/убрать лайк
        toggleLike: build.mutation<LikeResponse, LikeRequest>({
            query: ({ promptId, isLiked }) => ({
                url: '/like',
                method: 'POST',
                body: { promptId, isLiked },
            }),
            // Оптимистичное обновление кеша
            onQueryStarted: async ({ promptId, isLiked }, { dispatch, queryFulfilled }) => {
                const likeDelta = isLiked ? 1 : -1;

                // Обновляем кеш для конкретного промпта
                const promptPatchResult = dispatch(
                    promptApi.util.updateQueryData('getPrompt', { promptId }, draft => {
                        updatePromptLikes(draft, likeDelta, isLiked);
                    }),
                );

                // Обновляем кеш для похожих промптов
                const similarPromptsPatchResult = dispatch(
                    promptApi.util.updateQueryData('getSimilarPrompts', { promptId }, draft => {
                        const targetPrompt = draft?.find(prompt => prompt.id === promptId);
                        updatePromptLikes(targetPrompt, likeDelta, isLiked);
                    }),
                );

                // Обновляем кеш для популярных промптов
                const popularPromptsPatchResult = dispatch(
                    promptApi.util.updateQueryData('getPopularPrompts', undefined, draft => {
                        const targetPrompt = draft?.find(prompt => prompt.id === promptId);
                        updatePromptLikes(targetPrompt, likeDelta, isLiked);
                    }),
                );

                // Обновляем кеш для моих промптов
                const myPromptsPatchResult = dispatch(
                    promptApi.util.updateQueryData('getMyPrompts', undefined, draft => {
                        const targetPrompt = draft?.find(prompt => prompt.id === promptId);
                        updatePromptLikes(targetPrompt, likeDelta, isLiked);
                    }),
                );

                try {
                    await queryFulfilled;
                } catch {
                    // При ошибке откатываем все изменения
                    promptPatchResult.undo();
                    similarPromptsPatchResult.undo();
                    popularPromptsPatchResult.undo();
                    myPromptsPatchResult.undo();
                }
            },
        }),
    }),
});

export const {
    useGetPromptQuery,
    useGetSimilarPromptsQuery,
    useGetMyPromptsQuery,
    useGetPopularPromptsQuery,
    useToggleLikeMutation,
} = promptApi;
